import { message } from "antd";
import Axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosRequestTransformer,
  type AxiosResponse,
} from "axios";
import { format, isValid } from "date-fns";
import * as qs from "query-string";

import { isBTAdminPath } from "./btadmin-path";
import {
  APIError,
  BadRequestError,
  ConflictError,
  defaultErrorMessage,
  LoginRedirectError,
  NoAccessError,
  NotFoundError,
  ServiceUnavailableError,
  TooManyRequestsError,
  UpgradeRequiredError,
} from "./errors";

// Portal type matching server-side BTLoginPortalType — do not change values
enum PortalType {
  ALL = 0,
  BUILDER = 1,
  OWNER = 2,
  SUBS = 3,
  LEAD = 4,
  BTADMIN = 5,
}

// Only the portals the URL *proves* via a prefix. Builder URLs carry no prefix, so there is
// deliberately no BUILDER fallback: assuming BUILDER poisons the server session for a sub/owner
// on an unprefixed URL (mid-auth most of all), exactly like the ALL guess described at
// getPortalTypeWithFallback. Unproven means null — omit the header and let the server's
// session-derived login type decide. Mirrors the function of the same name in Clients.App.
function getProvenPortalTypeForUrl(url: string): PortalType | null {
  const lower = url.toLowerCase();
  if (lower.includes("/app/") && !lower.includes("/app/link")) {
    if (lower.includes("/app/subs/")) return PortalType.SUBS;
    if (lower.includes("/app/owner/") || lower.includes("/owner/ownerinvoice"))
      return PortalType.OWNER;
    if (lower.includes("/app/admin/")) return PortalType.BTADMIN;
    if (lower.includes("/app/share/")) return PortalType.LEAD;
  }
  return null;
}

/**
 * Null when unresolved — see {@link getPortalTypeWithFallback} for why that must not become `ALL`.
 *
 * Only the URL is consulted. This is the inline default for Node/Playwright hosts; browser
 * consumers inject the app's richer resolver via {@link setPortalResolver}, which reads the
 * resolved global-info snapshot.
 */
function getCurrentPortalType(): PortalType | null {
  return getProvenPortalTypeForUrl(window.location.pathname);
}

function handleRedirectToLogin() {
  const lpr = window.location.pathname + window.location.search;
  const loginRedirect = qs.stringifyUrl({
    url: isBTAdminPath() ? "/apix/v2/admin/auth/login" : "/",
    query: { lpr, reason: "session expired, please login" },
  });
  const windowToUse = window.top ?? window;
  windowToUse.location.assign(loginRedirect);
}

let currentAppVersion: string | null = null;
function checkForNewAppVersion(version: string) {
  const normalizedVersion = version.replace(/-blue|-green/g, "");
  if (!currentAppVersion) {
    currentAppVersion = normalizedVersion;
  } else if (currentAppVersion !== normalizedVersion) {
    currentAppVersion = normalizedVersion;
    try {
      localStorage.setItem(
        "bt-string-newAppDetectedDate",
        JSON.stringify(Date.now()),
      );
    } catch {
      // localStorage can throw (e.g. private mode / disabled storage); ignore.
    }
  }
}

function convertServerDateStringToDate(dateStr: string | undefined | null) {
  if (!dateStr) return undefined;
  const date = new Date(dateStr);
  if (!isValid(date)) return undefined;
  return date;
}

// Browser consumers inject richer portal/login/version/date behavior via DI so the
// package needn't import Clients.App modules; the inline defaults suit Node/Playwright.

// undefined means "unresolved, omit the header" — see Clients.App's getPortalTypeForHeader for
// why the header must never fall back to a guessed-but-wrong PortalType (ado-282449).
type PortalResolver = () => string | undefined;
type LoginRedirect = () => void;
type VersionHandler = (version: string) => void;
type DateReviver = (value: string) => Date | undefined;

let _portalResolver: PortalResolver | null = null;
let _loginRedirect: LoginRedirect | null = null;
let _versionHandler: VersionHandler | null = null;
let _dateReviver: DateReviver | null = null;

export function setPortalResolver(fn: PortalResolver | null): void {
  _portalResolver = fn;
}

export function setLoginRedirect(fn: LoginRedirect | null): void {
  _loginRedirect = fn;
}

export function setVersionHandler(fn: VersionHandler | null): void {
  _versionHandler = fn;
}

export function setDateReviver(fn: DateReviver | null): void {
  _dateReviver = fn;
}

// Returns undefined (never PortalType.ALL) when the portal can't be resolved, so the caller omits
// the header. `ALL` parses as a defined BTLoginTypes server-side, so PortalContextMiddleware would
// treat it as authoritative and permanently short-circuit SessionObject's fallback to the session's
// real login type — the same wrong-but-valid-header failure the app-level mutator avoids
// (ado-288217).
const getPortalTypeWithFallback = () => {
  try {
    return _portalResolver
      ? _portalResolver()
      : getCurrentPortalType()?.toString();
  } catch {
    return undefined;
  }
};

/**
 * This is the default transformResponse function used for APIx endpoints to handle parsing dates correctly.
 * This can be chained with other response transforms.
 * @example
 * ```ts
 * useBuildersGetV2({
 *    request: {
 *        transformResponse: [defaultTransformResponse, myCustomTransformResponse],
 *    },
 * });
 * @param data The data from the server or from previous response transforms
 * @returns object parsed from JSON string or the original data if not a string
 */
export function defaultTransformResponse(data: unknown) {
  if (data && typeof data === "string") {
    return JSON.parse(data, responseJsonParseReviver);
  }
  return data;
}

/**
 * This is the default transformRequest function used for APIx endpoints to handle serializing dates correctly.
 * This can be chained with other request transforms.
 * @example
 * ```ts
 * useBuildersGetV2({
 *     request: {
 *         transformRequest: [myCustomTransformRequest, defaultTransformRequest],
 *     },
 * });
 *
 * ```
 * @param data The data being sent to the server or from previous request transforms
 * @returns JSON string of object with dates formatted as expected by the API
 */
export const defaultTransformRequest: AxiosRequestTransformer = (
  data: unknown,
) => {
  if (data instanceof FormData) {
    return data;
  }
  return JSON.stringify(data, requestJsonStringifyReplacer);
};

const axiosInstance = Axios.create({
  // Apply default config to all apix requests
  paramsSerializer: function (params) {
    const newParams = prepareParamsForSerialization(params) as Record<
      string,
      unknown
    >;
    return qs.stringify(newParams, { arrayFormat: "none" });
  },
  transformRequest: defaultTransformRequest,
  transformResponse: defaultTransformResponse,
});

axiosInstance.interceptors.response.use(undefined, async (error) => {
  if (error.response) {
    const contentType = error.response.headers["content-type"];

    // Check if the response data is a Blob and possibly JSON
    if (
      error.response.data instanceof Blob &&
      contentType?.includes("application/json")
    ) {
      try {
        // Read and parse the Blob as JSON
        const text = await error.response.data.text();
        error.response.data = JSON.parse(text);
      } catch (parseError) {
        console.warn(
          "Failed to parse JSON from Blob error response:",
          parseError,
        );
      }
    }

    switch (error.response.status) {
      case 400:
        throw new BadRequestError(error.response.data);
      case 401:
        (_loginRedirect ?? handleRedirectToLogin)();
        break;
      case 402:
        throw new UpgradeRequiredError(error.response.data);
      case 403:
        throw new NoAccessError(error.response.data);
      case 404:
        throw new NotFoundError(error.response.data);
      case 409:
        throw new ConflictError(error.response.data);
      case 429:
        throw new TooManyRequestsError(
          error.response.data,
          error.response.data?.message,
        );
      case 503:
        throw new ServiceUnavailableError(
          error.response.data,
          error.response.data?.message,
          error.response.data?.retryAfterSeconds,
        );
      default:
        throw new APIError(error.response);
    }
  } else {
    throw error;
  }
});

// react version handling for front-end reloading
axiosInstance.interceptors.response.use((orgResponse) => {
  if (orgResponse?.headers) {
    let reqAppVersion = orgResponse.headers["version"];

    if (reqAppVersion && typeof reqAppVersion === "string") {
      reqAppVersion = reqAppVersion.split(",")[0]?.trim();
    }

    if (reqAppVersion) {
      (_versionHandler ?? checkForNewAppVersion)(reqAppVersion);
    }
  }

  return orgResponse;
});

// ─── Test transport override ──────────────────────────────────────────────────
// In Node.js test environments (e.g. Playwright), the browser's session cookies
// and implicit base URL are unavailable. Call setApixTransport() from the test
// setup to route all apixHandler calls through a Node.js-compatible transport.

type ApixTransport = (
  config: AxiosRequestConfig,
) => Promise<AxiosResponse<unknown>>;
let _transport: ApixTransport | null = null;

/**
 * Registers a transport override for all apixHandler calls.
 * Call this from test setup (e.g. a Playwright auto-fixture) to route APIX
 * requests through a session-authenticated Node.js-compatible transport.
 * Pass null to clear the override and restore the default axios behaviour.
 */
export function setApixTransport(transport: ApixTransport | null): void {
  _transport = transport;
}

// Orval-generated code passes `signal: AbortSignal | undefined` — widen the type so
// exactOptionalPropertyTypes doesn't reject it at the call site.
type ApixConfig = Omit<AxiosRequestConfig, "signal"> & {
  signal?: AbortSignal | undefined;
};

/**
 * DO NOT import this method manually, it is used by Orval to override the default axios instance.
 * Instead, import the generated code from "@btnet/services-btnet/handlers/<domain>/<file>"
 * if you want to use apix.
 */
export const apixHandler = <T>(
  config: ApixConfig,
  options?: Omit<AxiosRequestConfig, "cancelToken">,
): Promise<AxiosResponse<T>> => {
  // Destructure signal so we only include it when defined — spreading `signal: undefined`
  // violates exactOptionalPropertyTypes on AxiosRequestConfig.signal?: GenericAbortSignal.
  const { signal, ...restConfig } = config;
  const axiosConfig: AxiosRequestConfig =
    signal !== undefined ? { ...restConfig, signal } : { ...restConfig };

  if (_transport) {
    return _transport({ ...axiosConfig, ...options }) as Promise<
      AxiosResponse<T>
    >;
  }

  const source = Axios.CancelToken.source();
  const portalType = getPortalTypeWithFallback();
  const headers = {
    // Omitted (rather than defaulted to PortalType.ALL) when unresolved — an injected
    // resolver returning undefined means "unresolved", not "PortalType.ALL". See
    // Clients.App's getPortalTypeForHeader for why a wrong-but-valid header is worse than none.
    ...(portalType !== undefined ? { PortalType: portalType } : {}),
    ...restConfig.headers,
    ...options?.headers,
  };
  const promise = axiosInstance({
    ...axiosConfig,
    ...options,
    headers,
    cancelToken: source.token,
  });

  // @ts-expect-error promise.cancel is a non-standard cancellation hook attached for query cancellation
  promise.cancel = () => {
    source.cancel("Query was cancelled");
  };

  return promise;
};

export type ErrorType<Error> = AxiosError<Error>;
export type BodyType<BodyData> = BodyData;

const isoDateFormat =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d*)?(?:[-+]\d{2}:?\d{2}|Z)?$/;
function isIsoDateString(value: unknown): value is string {
  return typeof value === "string" && isoDateFormat.test(value);
}

function responseJsonParseReviver(_key: string, value: unknown) {
  if (isIsoDateString(value)) {
    return _dateReviver
      ? _dateReviver(value)
      : convertServerDateStringToDate(value);
  }
  return value;
}

/**
 * Replacer function used for JSON.stringify to handle serialization of requests.
 * @param key
 * @param data
 * @returns
 */
function requestJsonStringifyReplacer(
  _key: string,
  data: { [key: string]: unknown },
) {
  if (data === null || data === undefined || typeof data !== "object") {
    return data;
  }

  for (const [key, value] of Object.entries(data)) {
    if (value instanceof Date) {
      // Override the toJSON method to format the date as expected by the API
      data[key] = format(value, "yyyy-MM-dd'T'HH:mm:ss");
    }
  }

  return data;
}

/**
 * This method recursively searches through the body object and converts any date Objects to the expected api format. This
 * format does not contain the offset to preserve the date & time as seen by the user, which is expected to be in the builder's timezone.
 * @param body The data being read from the request
 */
function prepareParamsForSerialization(body: unknown): unknown {
  if (body === null || body === undefined || typeof body !== "object")
    return body;

  if (Array.isArray(body)) {
    return body.map((value) => {
      if (value instanceof Date) {
        return format(value, "yyyy-MM-dd'T'HH:mm:ss");
      }
      if (value && typeof value === "object") {
        return prepareParamsForSerialization(value);
      }
      return value;
    });
  }

  const result: Record<string, unknown> = {
    ...(body as Record<string, unknown>),
  };
  for (const [key, value] of Object.entries(result)) {
    if (value instanceof Date) {
      result[key] = format(value, "yyyy-MM-dd'T'HH:mm:ss");
    } else if (value && typeof value === "object") {
      result[key] = prepareParamsForSerialization(value);
    }
  }
  return result;
}

/**
 * Displays the error message from the server, if the server does not return an error message the default will be used
 * @param customMessage by default the message returned from the api will be shown, if the api failed but has no message "An error has occurred. Please try again." is shown
 */
export function showApixErrorMessage(
  e: Error,
  customMessage?: string,
  customDuration?: number,
) {
  if (e instanceof LoginRedirectError) {
    return;
  }

  const messageDuration = customDuration ?? 5;

  if (customMessage) {
    void message.error(customMessage, messageDuration);
    return;
  }

  const messageToDisplay: string = e.message || defaultErrorMessage;

  void message.error(messageToDisplay, messageDuration);
}
